import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { machineryService } from "@/services/machineryService";
import {
  CreateMachineryRequest,
  CreateMachineryShiftRequest,
  Machinery,
  MachineryShiftsResponse,
  UpdateDailyCapacityRequest,
  UpdateMachineryRequest,
  UpdateMachineryShiftRequest,
  UpdateTechnicalCapacityRequest,
} from "@/types/machinery";

interface ApiError {
  response?: {
    data?: {
      message?: string;
      body?: {
        message?: string;
      };
    };
  };
}

const getErrorMessage = (error: ApiError, fallback: string) => {
  return error.response?.data?.message || error.response?.data?.body?.message || fallback;
};

interface UseMachineryOptions {
  search?: string;
  status?: string;
  selectedMachineryId?: string | null;
}

export const useMachinery = (slug: string, options: UseMachineryOptions = {}) => {
  const queryClient = useQueryClient();
  const { search, status, selectedMachineryId } = options;

  const machineryQuery = useQuery({
    queryKey: ["machinery", slug, search || "", status || ""],
    queryFn: async (): Promise<Machinery[]> => {
      const response = await machineryService.getMachinery(slug, {
        search: search || undefined,
        status: status || undefined,
      });
      return response.data?.data || [];
    },
    enabled: !!slug,
  });

  const machineryShiftsQuery = useQuery({
    queryKey: ["machinery", "shifts", slug, selectedMachineryId || ""],
    queryFn: async (): Promise<MachineryShiftsResponse | null> => {
      if (!selectedMachineryId) return null;
      const response = await machineryService.listShifts(selectedMachineryId, slug);
      return response.data?.data || null;
    },
    enabled: !!slug && !!selectedMachineryId,
  });

  const invalidateMachineryQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["machinery"] });
  };

  const createMachinery = useMutation({
    mutationFn: (payload: CreateMachineryRequest) => machineryService.createMachinery(payload),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Maquinaria creada correctamente");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al crear maquinaria"));
    },
  });

  const updateMachinery = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMachineryRequest }) =>
      machineryService.updateMachinery(id, payload),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Maquinaria actualizada");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al actualizar maquinaria"));
    },
  });

  const deleteMachinery = useMutation({
    mutationFn: ({ id, company_id }: { id: string; company_id: string }) =>
      machineryService.deleteMachinery(id, slug, company_id),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Maquinaria desactivada");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al desactivar maquinaria"));
    },
  });

  const updateDailyCapacity = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDailyCapacityRequest }) =>
      machineryService.updateDailyCapacity(id, payload),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Capacidad diaria actualizada");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al actualizar capacidad diaria"));
    },
  });

  const updateTechnicalCapacity = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTechnicalCapacityRequest }) =>
      machineryService.updateTechnicalCapacity(id, payload),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Capacidad técnica actualizada");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al actualizar capacidad técnica"));
    },
  });

  const createShift = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateMachineryShiftRequest }) =>
      machineryService.createShift(id, payload),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Turno creado");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al crear turno"));
    },
  });

  const updateShift = useMutation({
    mutationFn: ({
      id,
      shiftId,
      payload,
    }: {
      id: string;
      shiftId: string;
      payload: UpdateMachineryShiftRequest;
    }) => machineryService.updateShift(id, shiftId, payload),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Turno actualizado");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al actualizar turno"));
    },
  });

  const deleteShift = useMutation({
    mutationFn: ({ id, shiftId, company_id }: { id: string; shiftId: string; company_id: string }) =>
      machineryService.deleteShift(id, shiftId, slug, company_id),
    onSuccess: () => {
      invalidateMachineryQueries();
      toast.success("Turno desactivado");
    },
    onError: (error: ApiError) => {
      toast.error(getErrorMessage(error, "Error al desactivar turno"));
    },
  });

  return {
    machinery: machineryQuery.data || [],
    isLoadingMachinery: machineryQuery.isLoading,
    selectedMachineryShiftConfig: machineryShiftsQuery.data,
    isLoadingSelectedShifts: machineryShiftsQuery.isLoading,
    createMachinery,
    updateMachinery,
    deleteMachinery,
    updateDailyCapacity,
    updateTechnicalCapacity,
    createShift,
    updateShift,
    deleteShift,
  };
};
